import type { ModelDateVersion, TaskModelSummary } from "@nexra/contracts";

export interface UnifiedModelGroup {
  familyKey: string;
  familyName: string;
  family: string;
  recommendedModel: TaskModelSummary;
  versions: TaskModelSummary[];
}

/**
 * Formats a raw model key or slug into clean user-facing format without provider prefix and with capitalized brand.
 * e.g., "anthropic/claude-sonnet-4-6" -> "Claude-sonnet-4-6"
 * e.g., "anthropic/claude-sonnet-5" -> "Claude-sonnet-5"
 * e.g., "bytedance/doubao-seedance-2-5-260628" -> "Doubao-seedance-2-5"
 * e.g., "bytedance/seed-2-0-lite-260228" -> "Seed-2-0-lite"
 * e.g., "openai/gpt-5.6-luna" -> "GPT-5.6-luna"
 */
export function formatModelSlug(slug: string, options?: { preserveDate?: boolean }): string {
  if (!slug) return "";
  const parts = slug.split("/");
  let raw = parts.length > 1 ? parts.slice(1).join("/") : slug;

  if (!options?.preserveDate) {
    // Strip trailing date snapshot suffix like -260628, -251215, -20260628
    raw = raw.replace(/-(20\d{6}|\d{6})$/, "");
  }

  // Capitalize brand / prefix
  if (/^claude-/i.test(raw)) return `Claude-${raw.slice(7)}`;
  if (/^gpt-/i.test(raw)) return `GPT-${raw.slice(4)}`;
  if (/^gemini-/i.test(raw)) return `Gemini-${raw.slice(7)}`;
  if (/^doubao-/i.test(raw)) return `Doubao-${raw.slice(7)}`;
  if (/^byteplus-/i.test(raw)) return `Byteplus-${raw.slice(9)}`;
  if (/^seed-/i.test(raw)) return `Seed-${raw.slice(5)}`;
  if (/^grok-/i.test(raw)) return `Grok-${raw.slice(5)}`;
  if (/^kimi-/i.test(raw)) return `Kimi-${raw.slice(5)}`;
  if (/^wan-/i.test(raw)) return `Wan-${raw.slice(4)}`;
  if (/^qwen-/i.test(raw)) return `Qwen-${raw.slice(5)}`;
  if (/^minimax-/i.test(raw)) return `MiniMax-${raw.slice(8)}`;
  if (/^happy-horse/i.test(raw)) return `Happy-horse${raw.slice(11)}`;

  const firstHyphen = raw.indexOf("-");
  if (firstHyphen > 0) {
    const brand = raw.slice(0, firstHyphen);
    return `${brand.charAt(0).toUpperCase() + brand.slice(1)}-${raw.slice(firstHyphen + 1)}`;
  }

  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/**
 * Returns a compact version badge label for a model, e.g. "v5.0", "v4.6", "v2.5", "v2"
 */
export function getVersionBadgeLabel(model: TaskModelSummary): string {
  if (model.versionLabel && model.versionLabel.trim()) {
    const vl = model.versionLabel.trim();
    return vl.toLowerCase().startsWith("v") ? vl : `v${vl}`;
  }
  const slug = (model.slug.split("/").pop() ?? model.slug).toLowerCase();
  const withoutDate = slug.replace(/-(20\d{6}|\d{6})$/, "");
  const vMatch = /(?:-|\b)v?(\d+(?:[.-]\d+)?)(?:-|$)/.exec(withoutDate);
  if (vMatch) {
    const vStr = vMatch[1].replace("-", ".");
    return `v${vStr}`;
  }
  return formatModelSlug(slug);
}

/**
 * Resolves the product line / family for a model based on the unified catalog taxonomy.
 */
export function getModelFamily(model: TaskModelSummary): string {
  const slug = model.slug.toLowerCase();
  const name = model.displayName.toLowerCase();

  // 1. Anthropic
  if (slug.includes("sonnet") || name.includes("sonnet")) return "Claude Sonnet";
  if (slug.includes("opus") || name.includes("opus")) return "Claude Opus";
  if (slug.includes("haiku") || name.includes("haiku")) return "Claude Haiku";
  if (slug.includes("fable") || name.includes("fable")) return "Claude Fable";

  // 2. OpenAI
  if (slug.includes("luna") || name.includes("luna")) return "GPT Luna";
  if (slug.includes("sol") || name.includes("sol")) return "GPT Sol";
  if (slug.includes("terra") || name.includes("terra")) return "GPT Terra";
  if (slug.includes("gpt-image") || name.includes("gpt image")) return "GPT Image";
  if (slug.includes("gpt-5") || name.includes("gpt-5") || slug.includes("gpt-4") || name.includes("gpt-4")) return "GPT 主系列";

  // 3. Google Gemini / Veo
  if (slug.includes("omni-flash") || name.includes("omni flash")) return "Gemini Omni Flash";
  if (slug.includes("gemini") || name.includes("gemini")) {
    if (slug.includes("flash-lite-image") || name.includes("flash-lite image")) return "Gemini Flash-Lite Image";
    if (slug.includes("flash-image") || name.includes("flash image")) return "Gemini Flash Image";
    if (slug.includes("pro-image") || name.includes("pro image")) return "Gemini Pro Image";
    if (slug.includes("pro") || name.includes("pro")) return "Gemini Pro";
    if (slug.includes("flash") || name.includes("flash")) return "Gemini Flash";
    return "Gemini";
  }
  if (slug.includes("veo") || name.includes("veo")) {
    if (slug.includes("fast") || name.includes("fast")) return "Veo Fast";
    if (slug.includes("lite") || name.includes("lite")) return "Veo Lite";
    return "Veo 标准版";
  }

  // 4. Bytedance / Seedance (BytePlus channels merge into corresponding Seedance specs)
  if (slug.includes("seedance") || name.includes("seedance")) {
    if (slug.includes("fast") || name.includes("fast")) return "Seedance Fast";
    if (slug.includes("mini") || name.includes("mini")) return "Seedance Mini";
    if (slug.includes("pro") || name.includes("pro")) return "Seedance Pro";
    return "Seedance 标准版";
  }

  // 5. Bytedance / Seedream (Lite vs Pro)
  if (slug.includes("seedream") || name.includes("seedream")) {
    if (slug.includes("pro") || name.includes("pro")) return "Doubao Seedream Pro";
    return "Doubao Seedream Lite";
  }

  // 6. Bytedance / Seed Language
  if (slug.includes("seed") || name.includes("seed")) {
    if (slug.includes("lite") || name.includes("lite")) return "Doubao Seed Lite";
    if (slug.includes("pro") || name.includes("pro")) return "Doubao Seed Pro";
    if (slug.includes("mini") || name.includes("mini")) return "Doubao Seed Mini";
    if (slug.includes("turbo") || name.includes("turbo")) return "Doubao Seed Turbo";
    return "Doubao Seed";
  }

  // 7. Doubao TTS & Embedding
  if (slug.includes("doubao-tts") || name.includes("doubao tts")) return "Doubao TTS";
  if (slug.includes("doubao-embedding-vision") || name.includes("doubao embedding vision")) return "Doubao Embedding Vision";

  // 8. Alibaba
  if (slug.includes("happy-horse") || name.includes("happyhorse") || name.includes("happy horse")) return "HappyHorse";
  if (slug.includes("qwen-image-edit-plus") || name.includes("qwen image edit plus")) return "Qwen Image Edit Plus";
  if (slug.includes("z-image-turbo") || name.includes("z-image turbo")) return "Z-Image Turbo";
  if (slug.includes("wan-") || name.includes("wan ")) {
    if (slug.includes("image") || name.includes("image")) return "Wan Image Pro";
    if (slug.includes("i2v") || name.includes("i2v")) return "Wan I2V";
    return "Wan Video";
  }

  // 9. Kling
  if (slug.includes("kling") || name.includes("kling")) {
    if (slug.includes("omni") || name.includes("omni")) return "Kling Omni";
    return "Kling 标准版";
  }

  // 10. MiniMax
  if (slug.includes("music") && slug.includes("minimax")) return "MiniMax Music";
  if (slug.includes("speech") || name.includes("speech")) {
    if (slug.includes("hd") || name.includes("hd")) return "MiniMax Speech HD";
    if (slug.includes("turbo") || name.includes("turbo")) return "MiniMax Speech Turbo";
    return "MiniMax Speech";
  }
  if (slug.includes("hailuo") || slug.includes("h23") || slug.includes("h3") || name.includes("hailuo")) {
    if (slug.includes("h23f") || slug.includes("fast")) return "MiniMax Hailuo Fast";
    return "MiniMax Hailuo";
  }

  // 11. PixVerse
  if (slug.includes("pc1") || slug.includes("pixverse-c")) return "PixVerse C 系列";
  if (slug.includes("pixverse") || slug.includes("p55") || slug.includes("p60") || name.includes("pixverse")) return "PixVerse 主系列";

  // 12. Vidu
  if (slug.includes("vidu") || slug.includes("vq3p")) return "Vidu Q Pro";

  // 13. HeyGen
  if (slug.includes("heygen")) return "HeyGen Photo Avatar";

  // 14. Midjourney / RunningHub
  if (slug.includes("youchuan") || slug.includes("midjourney") || name.includes("midjourney")) return "Midjourney";

  // 15. World Labs Marble
  if (slug.includes("marble") || name.includes("marble")) {
    if (slug.includes("plus") || name.includes("plus")) return "Marble Plus";
    if (slug.includes("draft") || name.includes("draft")) return "Marble Draft";
    return "Marble 标准版";
  }

  // 16. xAI
  if (slug.includes("grok-imagine-video")) return "Grok Video";
  if (slug.includes("grok-imagine-image")) {
    if (slug.includes("quality") || name.includes("quality")) return "Grok Image Quality";
    return "Grok Image";
  }
  if (slug.includes("grok") || name.includes("grok")) return "Grok";

  // 17. Moonshot Kimi
  if (slug.includes("kimi") || name.includes("kimi")) return "Kimi K2";

  // 18. ElevenLabs
  if (slug.includes("eleven") || slug.includes("music_v2")) {
    if (slug.includes("flash")) return "ElevenLabs Flash";
    if (slug.includes("multilingual")) return "ElevenLabs Multilingual";
    if (slug.includes("text_to_sound")) return "ElevenLabs Text to Sound";
    if (slug.includes("music")) return "ElevenLabs Music";
    if (slug.includes("v3")) return "ElevenLabs Eleven";
    return "ElevenLabs";
  }

  // 19. Fish Audio
  if (slug.includes("fish-audio") || slug.includes("s1") || slug.includes("s2pro")) {
    if (slug.includes("s2pro")) return "Fish Audio S Pro";
    return "Fish Audio S";
  }

  if (model.family && model.family.trim()) {
    return model.family.trim();
  }

  return model.displayName;
}

/**
 * Extracts a numeric score from a version string for ordering (higher is newer).
 * e.g., "5.0" -> 5.0, "4.8" -> 4.8, "4.6" -> 4.6, "v8.2" -> 8.2, "h3" -> 3.0, "p60" -> 6.0
 */
function versionScore(model: TaskModelSummary): number {
  if (model.isRecommended) return 10000;
  if (model.featured) return 5000;
  const slug = (model.slug.split("/").pop() ?? model.slug).toLowerCase();
  const versionStr = (model.versionLabel ?? slug).toLowerCase();

  // Special version handling
  if (slug.includes("minimax-h3")) return 300;
  if (slug.includes("h23")) return 23;
  if (slug.includes("p60")) return 60;
  if (slug.includes("p55")) return 55;
  if (slug.includes("v82")) return 82;
  if (slug.includes("v81")) return 81;
  if (slug.includes("v7")) return 70;
  if (slug === "xai/grok-imagine-video-1.5" || slug.includes("video-1.5")) return 15;
  if (slug === "xai/grok-imagine-video") return 10;

  // Strip date snapshots like 260628 or 20260628 so they do not overpower 2.1 vs 2.0
  const cleaned = versionStr.replace(/20\d{6}|\d{6}/g, "").replace(/(\d+)-(\d+)/g, "$1.$2");
  const match = /(\d+(?:\.\d+)?)/.exec(cleaned);
  return match ? parseFloat(match[1]) : 0;
}

/**
 * Groups models by product family for user-facing unified product line cards.
 */
export function groupModelsByFamily(models: readonly TaskModelSummary[]): UnifiedModelGroup[] {
  const groupsMap = new Map<string, TaskModelSummary[]>();

  for (const model of models) {
    const family = getModelFamily(model);
    const existing = groupsMap.get(family) ?? [];
    existing.push(model);
    groupsMap.set(family, existing);
  }

  const result: UnifiedModelGroup[] = [];

  for (const [familyName, familyModels] of groupsMap) {
    // 1. Exclude channel duplicate records (e.g. BytePlus Seedance or secondary vendor endpoints)
    const nonChannelModels = familyModels.filter((m) => {
      // Byteplus is a channel of Seedance
      if (m.slug.includes("byteplus-seedance")) {
        const hasCounterpart = familyModels.some((other) => !other.slug.includes("byteplus"));
        if (hasCounterpart) return false;
      }
      // veo-3-1-fast is a channel/alias of veo-3-1-fast-generate-001
      if (m.slug === "google/veo-3-1-fast") {
        const hasCounterpart = familyModels.some((other) => other.slug.includes("veo-3-1-fast-generate-001"));
        if (hasCounterpart) return false;
      }
      return true;
    });

    // 2. Check if there are models in this family that are date-suffixed versions of an existing base model
    const baseSlugSet = new Set<string>();
    for (const m of nonChannelModels) {
      const cleanSlug = m.slug.replace(/-(20\d{6}|\d{6})$/, "");
      if (cleanSlug === m.slug) {
        baseSlugSet.add(m.slug);
      }
    }

    const filteredModels = nonChannelModels.filter((m) => {
      const cleanSlug = m.slug.replace(/-(20\d{6}|\d{6})$/, "");
      if (cleanSlug !== m.slug && baseSlugSet.has(cleanSlug)) {
        return false;
      }
      return true;
    });

    // 3. Deduplicate by clean formatted slug within the same family so multiple date snapshots of same version don't produce duplicate cards
    const seenCleanSlugs = new Set<string>();
    const deduplicatedModels: TaskModelSummary[] = [];
    for (const m of filteredModels) {
      const key = formatModelSlug(m.slug);
      if (!seenCleanSlugs.has(key)) {
        seenCleanSlugs.add(key);
        deduplicatedModels.push(m);
      }
    }

    // 4. Sort so recommended / newest version comes first
    const sorted = deduplicatedModels.sort((a, b) => {
      if (a.isRecommended && !b.isRecommended) return -1;
      if (!a.isRecommended && b.isRecommended) return 1;
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return versionScore(b) - versionScore(a);
    });

    const recommended = sorted[0]!;
    result.push({
      familyKey: familyName.toLowerCase().replace(/\s+/g, "-"),
      familyName,
      family: familyName,
      recommendedModel: recommended,
      versions: sorted,
    });
  }

  return result;
}

/**
 * Extracts date snapshot versions from model metadata or public aliases.
 */
export function getModelDateVersions(model: TaskModelSummary): ModelDateVersion[] {
  if (model.dateVersions && model.dateVersions.length > 0) {
    return [...model.dateVersions];
  }
  const results: ModelDateVersion[] = [];
  for (const tag of model.tags ?? []) {
    const cleanTag = tag.startsWith("alias:") ? tag.slice(6) : tag;
    const dateMatch = /(\d{4}-?\d{2}-?\d{2}|\d{6})/.exec(cleanTag);
    if (dateMatch) {
      const rawDate = dateMatch[1];
      const formattedDate = rawDate.includes("-")
        ? rawDate
        : rawDate.length === 8
          ? `${rawDate.slice(0, 4)}-${rawDate.slice(4, 6)}-${rawDate.slice(6, 8)}`
          : rawDate.length === 6
            ? `20${rawDate.slice(0, 2)}-${rawDate.slice(2, 4)}-${rawDate.slice(4, 6)}`
            : rawDate;
      results.push({
        id: cleanTag,
        label: `${formattedDate} 快照`,
        date: formattedDate,
      });
    }
  }
  return results;
}

/**
 * Resolves sibling versions for a given model slug from the full catalog list.
 */
export function getSiblingVersions(
  currentModelSlug: string,
  allModels: readonly TaskModelSummary[]
): TaskModelSummary[] {
  if (!allModels || allModels.length === 0 || !currentModelSlug) return [];
  const groups = groupModelsByFamily(allModels);
  const currentCleanSlug = formatModelSlug(currentModelSlug);

  // 1. Find group containing currentModel (by slug or by cleanSlug)
  for (const g of groups) {
    const found = g.versions.some(
      (v) => v.slug === currentModelSlug || formatModelSlug(v.slug) === currentCleanSlug
    );
    if (found) {
      return g.versions;
    }
  }

  // 2. Fallback: match by getModelFamily
  const currentSummary = allModels.find(
    (m) => m.slug === currentModelSlug || formatModelSlug(m.slug) === currentCleanSlug
  );
  if (currentSummary) {
    const family = getModelFamily(currentSummary);
    const match = groups.find((g) => g.family === family);
    if (match) return match.versions;
  }

  return [];
}
